import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Mail, Lock, Key, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function LoginPage() {
  var auth = useAuth();
  var [isLogin, setIsLogin] = useState(true);
  var [forgot, setForgot] = useState(false);
  var [forgotSent, setForgotSent] = useState(false);
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [accessCode, setAccessCode] = useState('');
  var [loading, setLoading] = useState(false);
  var [localError, setLocalError] = useState('');

  function backToLogin() {
    setForgot(false);
    setForgotSent(false);
    setIsLogin(true);
    setLocalError('');
  }

  async function handleForgotSubmit(e) {
    e.preventDefault();
    setLocalError('');
    setLoading(true);
    try {
      var res = await fetch(API_URL + '/api/auth/request-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      });
      await res.json();
      setForgotSent(true);
    } catch (err) {
      setLocalError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLocalError('');
    setLoading(true);

    var result;
    if (isLogin) {
      result = await auth.login(email, password);
    } else {
      if (!accessCode.trim()) {
        setLocalError('Access code is required');
        setLoading(false);
        return;
      }
      result = await auth.register(email, password, accessCode);
    }

    setLoading(false);
    if (!result.success) {
      setLocalError(result.error);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-serif font-bold text-foreground">
            The Rules of Money
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Workbook</p>
        </div>

        {/* Form Card */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-card">
          <h2 className="text-xl font-semibold mb-6 text-center">
            {forgot ? 'Reset Your Password' : (isLogin ? 'Welcome Back' : 'Create Your Account')}
          </h2>

          {/* Error Message */}
          {localError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{localError}</span>
            </div>
          )}

          {forgotSent ? (
            <div className="text-center py-2" data-testid="forgot-sent">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm text-foreground">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link to that email.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Check your inbox (and spam folder). The link expires in 1 hour.
              </p>
              <button
                type="button"
                onClick={backToLogin}
                className="text-primary hover:underline font-medium text-sm mt-4"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </button>
            </div>
          ) : forgot ? (
            <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="forgot-form">
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a secure link to reset your password.
              </p>
              <div>
                <Label htmlFor="forgotEmail" className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4" /> Email
                </Label>
                <Input
                  id="forgotEmail"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  required
                  data-testid="forgot-email"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>) : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={backToLogin} className="text-primary hover:underline font-medium text-sm" data-testid="forgot-cancel">
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={function(e) { setEmail(e.target.value); }}
                required
                data-testid={isLogin ? "login-email" : "register-email"}
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                required
                minLength={6}
                data-testid={isLogin ? "login-password" : "register-password"}
              />
              {isLogin && (
                <div className="text-right mt-1.5">
                  <button
                    type="button"
                    onClick={function() { setForgot(true); setLocalError(''); }}
                    className="text-xs text-primary hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="accessCode" className="flex items-center gap-2 mb-2">
                  <Key className="h-4 w-4" />
                  Access Code
                </Label>
                <Input
                  id="accessCode"
                  type="text"
                  placeholder="Enter your access code"
                  value={accessCode}
                  onChange={function(e) { setAccessCode(e.target.value.toUpperCase()); }}
                  className="font-mono tracking-wider"
                  data-testid="register-access-code"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You received this code after your purchase
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid={isLogin ? "login-button" : "register-button"}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>
          )}

          {/* Toggle */}
          {!forgot && !forgotSent && (
          <div className="mt-6 text-center text-sm">
            {isLogin ? (
              <p className="text-muted-foreground">
                Have an access code?{' '}
                <button
                  type="button"
                  onClick={function() { setIsLogin(false); setLocalError(''); }}
                  className="text-primary hover:underline font-medium"
                >
                  Create account
                </button>
              </p>
            ) : (
              <p className="text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={function() { setIsLogin(true); setLocalError(''); }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Need an access code? Purchase the workbook to get started.
        </p>
      </div>
    </div>
  );
}

export { LoginPage };
